import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { OrdersNotClosedAlert } from "src/pages/App/pages/Organization/components/OrdersNotClosedAlert";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { ArchivesApi, ArchivesStatus } from "src/services/Api/Organization/types";
import { getErrorOrderNotClosed } from "src/utils/ordersNotClosedAlert";
import { OrderNotClosedApiError } from "src/utils/types";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { waitForArchiveStatusChange } from "../../utils";

interface PerformActionArgs {
	action: (dateTo: string, organizationId?: number) => Promise<ArchivesApi>;
	setLoading: (loading: boolean) => void;
}

export const ArchiveForm = () => {
	const [closeLoading, setCloseLoading] = useState(false);
	const [prepareArchiveLoading, setPrepareArchiveLoading] = useState(false);
	const [archiveGenerationInProgress, setArchiveGenerationInProgress] = useState(false);
	const [orderNotClosedError, setOrderNotClosedError] = useState<OrderNotClosedApiError | undefined>(undefined);
	const history = useHistory();
	const form = useForm<{ dateTo: string }>();
	const {
		control,
		formState: { errors },
		watch,
	} = form;
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const { id: organizationId } = useSelector(selectOrganization);

	const performAction = async ({ action, setLoading }: PerformActionArgs) => {
		try {
			setLoading(true);
			const dateTo = watch("dateTo");
			const { id: archiveId } = await action(dateTo, organizationId);
			setArchiveGenerationInProgress(true);

			await waitForArchiveStatusChange(
				archiveId,
				(updatedStatus: ArchivesStatus) => updatedStatus === "OPEN" || updatedStatus === "CLOSED"
			);

			setArchiveGenerationInProgress(false);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
			history.push(`/${organizationId}/settings/archives/${archiveId}`);
		} catch (err: any) {
			const errorOrderNotClosed = getErrorOrderNotClosed(err);

			setOrderNotClosedError(errorOrderNotClosed);
			handleError.alert(err, addFlash);
		} finally {
			setLoading(false);
		}
	};

	const archiveCloseHandler = () =>
		performAction({
			action: apiOrganization.closeArchiveDateTo,
			setLoading: setCloseLoading,
		});

	const prepareArchiveHandler = () =>
		performAction({
			action: apiOrganization.prepareArchive,
			setLoading: setPrepareArchiveLoading,
		});

	return (
		<div>
			{orderNotClosedError && <OrdersNotClosedAlert className="my-2" orderNotClosedError={orderNotClosedError} />}
			<h5 className="mb-1">{t("modules.archives.action.close_archive.title")}</h5>
			<p className="text-muted">{t("modules.archives.action.close_archive.help_text.title")}</p>
			<div className="row">
				<div className="col-6">
					<FormDatePicker
						control={control}
						errors={errors}
						name="dateTo"
						label={t("modules.archives.field.date_to.title")}
					/>
				</div>
			</div>
			<ButtonLoading loading={closeLoading} className="me-2" onClick={archiveCloseHandler}>
				{t("common.action.close", { ns: "lib" })}
			</ButtonLoading>
			<ButtonLoading loading={prepareArchiveLoading} onClick={prepareArchiveHandler}>
				{t("common.action.create", { ns: "lib" })}
			</ButtonLoading>
			{archiveGenerationInProgress && (
				<span className="d-block mt-2 font-weight-bold display-6" style={{ fontSize: "18px" }}>
					{`${t("modules.archives.action.archive_generation_in_progress.title")}...`}
				</span>
			)}
		</div>
	);
};
